<script setup>
import ToggleQuotationToCallAction from '@frontJs/components/actions/projectManagement/apikom/ToggleQuotationToCallAction.vue';
import BellIcon from '@iconify-vue/ph/bell';
import BellRingingIcon from '@iconify-vue/ph/bell-ringing';
import CurrencyEurIcon from '@iconify-vue/ph/currency-eur';
import FileTextIcon from '@iconify-vue/ph/file-text';
import NotePencilIcon from '@iconify-vue/ph/note-pencil';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import DangerIconCell from '@js/components/tables/cells/DangerIconCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastSaved } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const props = defineProps({
    userSessionKey: {
        type: String,
        required: false,
        default: null,
    },
    ongoing: {
        type: Boolean,
        required: false,
        default: false,
    },
    won: {
        type: Boolean,
        required: false,
        default: false,
    },
    toInvoice: {
        type: Boolean,
        required: false,
        default: false,
    },
    invoiced: {
        type: Boolean,
        required: false,
        default: false,
    },
    partiallyInvoiced: {
        type: Boolean,
        required: false,
        default: false,
    },
    paid: {
        type: Boolean,
        required: false,
        default: false,
    },
    closed: {
        type: Boolean,
        required: false,
        default: false,
    },
    lost: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            header: () => 'ID',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            header: () => 'Date',
            cell: (props) =>
                h(TextCell, {
                    text: formatDate(props.row.original.date),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'customer',
            header: () => 'Client',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.customer,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            header: () => 'Type',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.type,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'users',
            header: () => 'Responsable',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.users
                        .map((user) => user.fullName)
                        .join(', '),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sentAt',
            header: () => 'Envoyé le',
            cell: (props) =>
                props.row.original.sentAt
                    ? h(TextCell, {
                          text: formatDate(props.row.original.sentAt),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'calledAt',
            header: () => 'Dernière relance',
            cell: (props) =>
                props.row.original.calledAt
                    ? h(TextCell, {
                          text: formatDate(props.row.original.calledAt),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'toCall',
            header: () => 'À relancer',
            cell: (props) =>
                props.row.original.toCall ? h(DangerIconCell) : null,
        }),
        columnHelper.display({
            id: 'details',
            header: 'Détails',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.comment
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Commentaire',
                                          content: props.row.original.comment,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir le commentaire',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h('div', { class: 'size-8' }, ' '),
                            props.row.original.link
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      title: 'Voir le lien du devis',
                                      type: 'primary',
                                      href: props.row.original.link,
                                      target: '_blank',
                                      icon: CurrencyEurIcon,
                                  })
                                : h('div', { class: 'size-8' }, ' '),
                            props.row.original.signedLink
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      title: 'Voir le lien du devis signé',
                                      type: 'primary',
                                      href: props.row.original.signedLink,
                                      target: '_blank',
                                      icon: NotePencilIcon,
                                  })
                                : h('div', { class: 'size-8' }, ' '),
                        ],
                    },
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                ToggleQuotationToCallAction,
                                {
                                    quotation: props.row.original,
                                    callback: () => {
                                        toastSaved();
                                        updateData();
                                    },
                                },
                                {
                                    button: ({ action }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            type: 'secondary',
                                            square: true,
                                            callback: () => action(),
                                            title: props.row.original.toCall
                                                ? 'Ne doit pas être relancé'
                                                : 'Doit être relancé',
                                            icon: props.row.original.toCall
                                                ? BellIcon
                                                : BellRingingIcon,
                                        }),
                                },
                            ),
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.apikom.updateQuotation',
                                    {
                                        quotation: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.apikom.postQuotations'),
    data: {
        ongoing: props.ongoing,
        won: props.won,
        toInvoice: props.toInvoice,
        invoiced: props.invoiced,
        partiallyInvoiced: props.partiallyInvoiced,
        paid: props.paid,
        closed: props.closed,
        lost: props.lost,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="route('front.projectManagement.apikom.createQuotation')"
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
